// src/store/slices/adminFeedbackSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import adminFeedbackService from '../../services/adminFeedbackService';

// Async thunks
export const fetchAllFeedback = createAsyncThunk(
  'adminFeedback/fetchAll',
  async ({ page = 0, size = 20, sortBy = 'createdAt', sortDirection = 'desc', status = '' }, { rejectWithValue }) => {
    try {
      const response = await adminFeedbackService.getAllFeedback(page, size, sortBy, sortDirection, status);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// НОВ: Вземете фийдбак по ID
export const fetchFeedbackById = createAsyncThunk(
  'adminFeedback/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminFeedbackService.getFeedbackById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchFeedbackStats = createAsyncThunk(
  'adminFeedback/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminFeedbackService.getFeedbackStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateFeedbackStatus = createAsyncThunk(
  'adminFeedback/updateStatus',
  async ({ id, status, processedBy = 'admin', notes = '' }, { rejectWithValue }) => {
    try {
      const response = await adminFeedbackService.updateFeedbackStatus(id, status, processedBy, notes);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteFeedback = createAsyncThunk(
  'adminFeedback/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminFeedbackService.deleteFeedback(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const searchFeedback = createAsyncThunk(
  'adminFeedback/search',
  async ({ email, chitalishteName, feedbackType, page = 0, size = 20 }, { rejectWithValue }) => {
    try {
      const response = await adminFeedbackService.searchFeedback(email, chitalishteName, feedbackType, page, size);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const adminFeedbackSlice = createSlice({
  name: 'adminFeedback',
  initialState: {
    feedback: [],
    currentFeedback: null,
    stats: null,
    loading: false,
    error: null,
    pagination: {
      page: 0,
      size: 20,
      totalPages: 0,
      totalElements: 0
    }
  },
  reducers: {
    setCurrentFeedback: (state, action) => {
      state.currentFeedback = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetFeedbackState: (state) => {
      state.feedback = [];
      state.currentFeedback = null;
      state.stats = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Feedback
      .addCase(fetchAllFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllFeedback.fulfilled, (state, action) => {
        state.loading = false;
        state.feedback = action.payload.content || action.payload;
        state.pagination = {
          page: action.payload.pageable?.pageNumber || 0,
          size: action.payload.pageable?.pageSize || 20,
          totalPages: action.payload.totalPages || 1,
          totalElements: action.payload.totalElements || action.payload.length || 0
        };
      })
      .addCase(fetchAllFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // НОВ: Fetch Feedback By ID
      .addCase(fetchFeedbackById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeedbackById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentFeedback = action.payload;
      })
      .addCase(fetchFeedbackById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Stats
      .addCase(fetchFeedbackStats.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFeedbackStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchFeedbackStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Status
      .addCase(updateFeedbackStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateFeedbackStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedFeedback = action.payload;
        const index = state.feedback.findIndex(f => f.id === updatedFeedback.id);
        if (index !== -1) {
          state.feedback[index] = updatedFeedback;
        }
        if (state.currentFeedback?.id === updatedFeedback.id) {
          state.currentFeedback = updatedFeedback;
        }
      })
      .addCase(updateFeedbackStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete Feedback
      .addCase(deleteFeedback.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteFeedback.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload?.id || action.payload;
        state.feedback = state.feedback.filter(f => f.id !== deletedId);
        if (state.currentFeedback?.id === deletedId) {
          state.currentFeedback = null;
        }
      })
      .addCase(deleteFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Search Feedback
      .addCase(searchFeedback.pending, (state) => {
        state.loading = true;
      })
      .addCase(searchFeedback.fulfilled, (state, action) => {
        state.loading = false;
        state.feedback = action.payload.content || action.payload;
        state.pagination = {
          page: action.payload.pageable?.pageNumber || 0,
          size: action.payload.pageable?.pageSize || 20,
          totalPages: action.payload.totalPages || 1,
          totalElements: action.payload.totalElements || action.payload.length || 0
        };
      })
      .addCase(searchFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setCurrentFeedback, clearError, resetFeedbackState } = adminFeedbackSlice.actions;

export const selectAdminFeedback = (state) => state.adminFeedback.feedback;
export const selectCurrentFeedback = (state) => state.adminFeedback.currentFeedback;
export const selectFeedbackStats = (state) => state.adminFeedback.stats;
export const selectFeedbackLoading = (state) => state.adminFeedback.loading;
export const selectFeedbackError = (state) => state.adminFeedback.error;
export const selectFeedbackPagination = (state) => state.adminFeedback.pagination;

export default adminFeedbackSlice.reducer;